import React, { useCallback, useEffect, useState } from 'react';
import { Button, Image, Input, InputNumber, Space, Switch, Tag, Typography, message } from 'antd';
import { DownloadOutlined, QrcodeOutlined } from '@ant-design/icons';
import PageTitle from '../../components/PageTitle';
import ListToolbar from '../../components/ListToolbar';
import ResizableTable from '../../components/ResizableTable';
import {
  downloadRentQRCode,
  generateRentQRCode,
  getRentQRCodes,
  updateRentQRCode,
} from '../../api';

const { Text, Link } = Typography;

const RentQRCodeList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchList = useCallback(async (page = 1, pageSize = 20, search = '') => {
    setLoading(true);
    try {
      const res = await getRentQRCodes({ page, page_size: pageSize, search });
      if (res?.success) {
        const payload = res.data || {};
        setData(Array.isArray(payload.list) ? payload.list : []);
        setPagination({
          current: payload.page || page,
          pageSize: payload.page_size || pageSize,
          total: payload.total || 0,
        });
      } else {
        message.error(res?.msg || '加载失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(1, pagination.pageSize, '');
  }, [fetchList]);

  const handleSearch = () => {
    fetchList(1, pagination.pageSize, searchText.trim());
  };

  const handleReset = () => {
    setSearchText('');
    fetchList(1, pagination.pageSize, '');
  };

  const handleRefresh = () => {
    fetchList(pagination.current, pagination.pageSize, searchText.trim());
  };

  const patchRow = (id, patch) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleStatusChange = async (record, checked) => {
    const status = checked ? 1 : 0;
    try {
      const res = await updateRentQRCode(record.id, { status });
      if (res?.success) {
        patchRow(record.id, res.data || { status });
        message.success('状态已更新');
      } else {
        message.error(res?.msg || '更新失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '更新失败');
    }
  };

  const handleHouseIdSave = async (record, value) => {
    const nextVal = value === '' || value == null ? null : Number(value);
    const prevVal = record.house_id == null ? null : Number(record.house_id);
    if (nextVal === prevVal) return;
    if (nextVal != null && (!Number.isFinite(nextVal) || nextVal <= 0)) {
      message.warning('房源 ID 须为正整数，留空表示未绑定');
      return;
    }
    try {
      const res = await updateRentQRCode(record.id, { house_id: nextVal });
      if (res?.success) {
        patchRow(record.id, res.data || { house_id: nextVal });
        message.success('房源 ID 已更新');
      } else {
        message.error(res?.msg || '更新失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '更新失败');
    }
  };

  const handleGenerate = async (record) => {
    setGeneratingId(record.id);
    try {
      const res = await generateRentQRCode(record.id);
      if (res?.success) {
        patchRow(record.id, res.data || {});
        if (res.saved === false) {
          message.warning(res.msg || '二维码已生成但未保存到数据库');
        } else {
          message.success(res.msg || '二维码已生成');
        }
      } else {
        message.error(res?.msg || '生成失败');
      }
    } catch (err) {
      const detail = err.response?.data?.msg || err.response?.data?.error || err.message;
      message.error(detail ? `生成失败：${detail}` : '生成失败');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      await downloadRentQRCode(record.id, record.code);
      message.success('下载成功');
    } catch (err) {
      message.error(err?.message || '下载失败');
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      title: '序号',
      key: 'seq',
      width: 70,
      fixed: 'left',
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: '招租编码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left',
      sorter: false,
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: '房源 ID',
      dataIndex: 'house_id',
      key: 'house_id',
      width: 140,
      render: (val, record) => (
        <InputNumber
          style={{ width: '100%' }}
          min={1}
          precision={0}
          placeholder="未绑定"
          value={val ?? undefined}
          onChange={(v) => patchRow(record.id, { house_id: v ?? null })}
          onBlur={(e) => {
            const raw = String(e.target.value || '').trim();
            handleHouseIdSave(record, raw === '' ? null : raw);
          }}
          onPressEnter={(e) => {
            const raw = String(e.target.value || '').trim();
            handleHouseIdSave(record, raw === '' ? null : raw);
          }}
        />
      ),
    },
    {
      title: '是否生效',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val, record) => (
        <Switch
          checked={Number(val) === 1}
          checkedChildren="启用"
          unCheckedChildren="停用"
          onChange={(checked) => handleStatusChange(record, checked)}
        />
      ),
    },
    {
      title: '二维码链接',
      dataIndex: 'qr_url',
      key: 'qr_url',
      width: 280,
      ellipsis: true,
      render: (val) => (
        <Link href={val} target="_blank" copyable={{ text: val }}>
          {val}
        </Link>
      ),
    },
    {
      title: '二维码',
      dataIndex: 'qrcode_base64',
      key: 'qrcode',
      width: 120,
      render: (val, record) => {
        if (!val && !record.has_qrcode) {
          return <Text type="secondary">未生成</Text>;
        }
        const src = val ? `data:image/png;base64,${val}` : '';
        return src ? (
          <Image
            src={src}
            alt={record.code}
            width={80}
            height={80}
            preview={{ src }}
            style={{ objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4 }}
          />
        ) : (
          <Text type="secondary">已生成</Text>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<QrcodeOutlined />}
            loading={generatingId === record.id}
            onClick={() => handleGenerate(record)}
          >
            生成
          </Button>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            loading={downloadingId === record.id}
            onClick={() => handleDownload(record)}
          >
            下载
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="二维码生成" />
      <ListToolbar
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        searchPlaceholder="搜索编码或房源 ID"
        beforeFilters={<Tag color="blue">链接：https://chuzu.spsjkj.com?scene=编码</Tag>}
      />
      <ResizableTable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        storageKey="rentQRCodeList_columnStates"
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            fetchList(page, pageSize, searchText.trim());
          },
        }}
      />
    </div>
  );
};

export default RentQRCodeList;
